'use client';

import type { ReactNode } from 'react';
import { EndpointsRenderer } from '../../ToolCallsRenderers/renderers/EndpointsRenderer';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import { PlainTextRenderer } from '../../ToolCallsRenderers/renderers/PlainTextRenderer';
import {
  parseEndpointsListFromXML,
  parseEndpointsFromPipeList,
  parseEndpointFromXML,
} from '../../content-utils/content-parsers';
import type { OutputRenderContext } from '../../tool-config-types';
import type { ParsedEndpoint } from '../../ToolCallsRenderers/types';

const parseEndpointFromMarkdown = (content: string): ParsedEndpoint | null => {
  const idMatch = content.match(
    /(?:^|\b)(?:\*\*)?ID(?::\*\*|\*\*:|:)\s*([a-f0-9]{24})/i,
  );
  const urlMatch = content.match(
    /(?:^|\b)(?:\*\*)?URL(?::\*\*|\*\*:|:)\s*(https?:\/\/\S+|\/\S+)/i,
  );
  const methodMatch = content.match(
    /\b(?:Method|Request):\s*(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD)\b/i,
  );

  if (!idMatch && !urlMatch) {
    return null;
  }

  return {
    endpointId: idMatch?.[1],
    endpointUrl: urlMatch?.[1],
    method: methodMatch?.[1]?.toUpperCase(),
  };
};

const parseEndpointsFromMarkdown = (
  content: string,
): ParsedEndpoint[] | null => {
  const itemPattern =
    /-\s+\*\*(?:(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD|UNKNOWN)\s+)?(.*?)\*\*\s*\n\s*ID:\s*([^\n]+)(?:\n\s*URL:\s*([^\n]+))?(?:\n\s*Description:\s*([^\n]+))?/gi;
  const endpoints: ParsedEndpoint[] = [];

  for (const match of content.matchAll(itemPattern)) {
    endpoints.push({
      method: match[1]?.trim(),
      path: match[2]?.trim(),
      endpointId: match[3]?.trim(),
      endpointUrl: match[4]?.trim(),
      endpointDescription: match[5]?.trim(),
    });
  }

  return endpoints.length > 0 ? endpoints : null;
};

const parseEndpointFromObject = (content: object): ParsedEndpoint | null => {
  const record = content as Record<string, unknown>;
  const endpointRecord =
    typeof record.endpoint === 'object' && record.endpoint !== null
      ? (record.endpoint as Record<string, unknown>)
      : typeof record.endpointDetails === 'object' &&
          record.endpointDetails !== null
        ? (record.endpointDetails as Record<string, unknown>)
        : record;
  const id = endpointRecord.id ?? endpointRecord.endpointId;
  const url = endpointRecord.url ?? endpointRecord.endpointUrl;
  const path = endpointRecord.path;
  const request = endpointRecord.request;
  const method =
    typeof endpointRecord.method === 'string'
      ? endpointRecord.method
      : typeof request === 'object' &&
          request !== null &&
          typeof (request as Record<string, unknown>).method === 'string'
        ? ((request as Record<string, unknown>).method as string)
        : undefined;

  if (
    typeof id !== 'string' &&
    typeof url !== 'string' &&
    typeof path !== 'string'
  ) {
    return null;
  }

  return {
    endpointId: typeof id === 'string' ? id : undefined,
    endpointUrl: typeof url === 'string' ? url : undefined,
    path: typeof path === 'string' ? path : undefined,
    method,
    endpointDescription:
      typeof endpointRecord.description === 'string'
        ? endpointRecord.description
        : undefined,
  };
};

const parseEndpointsFromObject = (content: object): ParsedEndpoint[] | null => {
  const record = content as Record<string, unknown>;
  const value = Array.isArray(record.endpoints)
    ? record.endpoints
    : Array.isArray(record.results)
      ? record.results
      : undefined;

  if (!value) {
    return null;
  }

  const endpoints = value
    .map((item) =>
      typeof item === 'object' && item !== null
        ? parseEndpointFromObject(item)
        : null,
    )
    .filter((item): item is ParsedEndpoint => item !== null);

  return endpoints.length > 0 ? endpoints : null;
};

export const renderEndpointsListResult = (
  ctx: OutputRenderContext,
): ReactNode => {
  const { content, maxHeight, onOpenObject } = ctx;
  const parsed = parseEndpointsListContent(content);
  const endpoints = parsed?.endpoints ?? null;
  const rawContent = parsed?.rawContent;

  if (endpoints && endpoints.length > 0) {
    return (
      <EndpointsRenderer
        endpoints={endpoints}
        rawContent={rawContent}
        maxHeight={maxHeight}
        onOpenObject={onOpenObject}
      />
    );
  }

  if (typeof content === 'object') {
    return <JSONRenderer data={content} maxHeight={maxHeight} />;
  }

  return <PlainTextRenderer content={String(content)} maxHeight={maxHeight} />;
};

export const renderEndpointResult = (ctx: OutputRenderContext): ReactNode => {
  const { content, maxHeight, onOpenObject } = ctx;
  const parsed = parseEndpointContent(content);
  const endpoint = parsed?.endpoint ?? null;
  const rawContent = parsed?.rawContent;

  if (endpoint) {
    return (
      <EndpointsRenderer
        endpoints={[endpoint]}
        rawContent={rawContent}
        maxHeight={maxHeight}
        onOpenObject={onOpenObject}
      />
    );
  }

  if (typeof content === 'object') {
    return <JSONRenderer data={content} maxHeight={maxHeight} />;
  }

  return <PlainTextRenderer content={String(content)} maxHeight={maxHeight} />;
};

export function parseEndpointsListContent(content: unknown): {
  endpoints: ParsedEndpoint[];
  rawContent?: string;
} | null {
  if (typeof content === 'string') {
    let endpoints = parseEndpointsListFromXML(content);

    endpoints ??= parseEndpointsFromPipeList(content);
    endpoints ??= parseEndpointsFromMarkdown(content);
    const endpoint =
      parseEndpointFromXML(content) ?? parseEndpointFromMarkdown(content);

    endpoints ??= endpoint ? [endpoint] : null;
    if (!endpoints || endpoints.length === 0) {
      return null;
    }

    return {
      endpoints,
      rawContent: content,
    };
  }

  if (typeof content === 'object' && content !== null) {
    const endpoints = parseEndpointsFromObject(content);

    if (!endpoints || endpoints.length === 0) {
      return null;
    }

    return {
      endpoints,
      rawContent: JSON.stringify(content, null, 2),
    };
  }

  return null;
}

export function parseEndpointContent(content: unknown): {
  endpoint: ParsedEndpoint;
  rawContent?: string;
} | null {
  if (typeof content === 'string') {
    const endpoint =
      parseEndpointFromXML(content) ?? parseEndpointFromMarkdown(content);

    if (!endpoint) {
      return null;
    }

    return {
      endpoint,
      rawContent: content,
    };
  }

  if (typeof content === 'object' && content !== null) {
    const endpoint = parseEndpointFromObject(content);

    if (!endpoint) {
      return null;
    }

    return {
      endpoint,
      rawContent: JSON.stringify(content, null, 2),
    };
  }

  return null;
}
